import { useEffect, useMemo, useState } from 'react'
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Box, Button, Text, HStack, Spinner
} from '@chakra-ui/react'
import { FaFolder, FaHome } from 'react-icons/fa'
import { theme } from 'styles'
import { getStorageTree, moveStorageFile, moveStorageFolder } from 'services/storage'
import { errorOf } from './storageUtils'

// Folder picker for moving a file or folder. Admins see the whole tree with
// "Home" at the top; other users see only the folders they can edit.
const MoveModal = ({ isOpen, onClose, item, isAdmin, onMoved }) => {
  const [tree, setTree] = useState(null)
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(undefined) // null = Home
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setSelected(undefined)
    setError(null)
    setLoading(true)
    getStorageTree().then(res => {
      if (errorOf(res)) setError(errorOf(res))
      else setTree(res.data)
      setLoading(false)
    })
  }, [isOpen])

  const currentParent = item ? (item.kind === 'folder' ? item.parent : item.folder) || null : null

  // Folders that can't be a destination: the folder being moved and
  // everything under it.
  const blocked = useMemo(() => {
    const set = new Set()
    if (item && item.kind === 'folder' && tree) {
      set.add(String(item._id))
      tree.folders.forEach(f => {
        if ((f.ancestors || []).some(a => String(a) === String(item._id))) set.add(String(f._id))
      })
    }
    return set
  }, [item, tree])

  const rows = useMemo(() => {
    if (!tree) return []
    const byParent = {}
    tree.folders.forEach(f => {
      const key = f.parent ? String(f.parent) : 'root'
      byParent[key] = byParent[key] || []
      byParent[key].push(f)
    })
    const out = []
    const walk = (parentKey, depth) => {
      (byParent[parentKey] || []).forEach(f => {
        out.push({ ...f, depth })
        walk(String(f._id), depth + 1)
      })
    }
    if (tree.roots) {
      // Non-admin: start from each granted top folder.
      const rootIds = tree.roots.map(String)
      tree.folders.filter(f => rootIds.includes(String(f._id))).forEach(f => {
        out.push({ ...f, depth: 0 })
        walk(String(f._id), 1)
      })
    } else {
      walk('root', 0)
    }
    return out
  }, [tree])

  const submit = async () => {
    if (selected === undefined) return
    setSaving(true)
    const res = item.kind === 'folder'
      ? await moveStorageFolder(item._id, selected)
      : await moveStorageFile(item._id, selected)
    setSaving(false)
    const err = errorOf(res, 'Could not move')
    if (err) { setError(err); return }
    onMoved(res.data, selected)
    onClose()
  }

  const Row = ({ id, name, depth, icon, disabled, isCurrent }) => {
    const isSelected = selected !== undefined && String(selected) === String(id)
    return (
      <HStack
        as="button" type="button" w="100%" textAlign="left" px={3} py={2} spacing={3}
        pl={`${12 + depth * 20}px`}
        bg={isSelected ? theme.greenTint : 'transparent'}
        borderLeftWidth="3px" borderColor={isSelected ? theme.green : 'transparent'}
        opacity={disabled ? 0.45 : 1}
        cursor={disabled ? 'not-allowed' : 'pointer'}
        _hover={disabled ? {} : { bg: theme.greenTint }}
        onClick={() => !disabled && setSelected(id)}
        disabled={disabled}
        data-testid={`move-target-${id === null ? 'home' : id}`}
      >
        {icon}
        <Text fontSize="sm" noOfLines={1}>{name}</Text>
        {isCurrent && <Text fontSize="xs" color={theme.muted}>(current location)</Text>}
      </HStack>
    )
  }

  return (
    <Modal isOpen={isOpen} onClose={() => !saving && onClose()} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Move “{item && item.name}”</ModalHeader>
        <ModalCloseButton />
        <ModalBody px={2}>
          {loading && <Box textAlign="center" py={6}><Spinner /></Box>}
          {!loading && tree && (
            <Box borderWidth="1px" borderColor={theme.line} borderRadius="md" overflow="hidden">
              {isAdmin && (
                <Row id={null} name="Home" depth={0} icon={<FaHome color={theme.green} />} isCurrent={currentParent === null} />
              )}
              {rows.map(f => (
                <Row
                  key={f._id} id={f._id} name={f.name} depth={f.depth + (isAdmin ? 1 : 0)}
                  icon={<FaFolder color="#d9a441" />}
                  disabled={blocked.has(String(f._id))}
                  isCurrent={String(currentParent) === String(f._id)}
                />
              ))}
              {!rows.length && !isAdmin && (
                <Text p={4} fontSize="sm" color={theme.muted}>There are no other folders you can move this into.</Text>
              )}
            </Box>
          )}
          {error && <Text mt={3} px={2} fontSize="sm" color={theme.danger}>{error}</Text>}
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose} isDisabled={saving}>Cancel</Button>
          <Button
            colorScheme="green" onClick={submit} isLoading={saving}
            isDisabled={selected === undefined || String(selected) === String(currentParent)}
          >
            Move here
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default MoveModal
